import React, { useEffect, useState, useRef } from 'react';
import { Card } from './ui/Card';
import { Eye, Download, MoreVertical, AlertCircle, Filter, Info } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
interface StatCardProps {
  title: string;
  subtitle?: string;
  count?: number;
  actionLabel?: string;
  actionLink?: string;
  onQuickView?: () => void;
  onSolveIssues?: () => void;
  children: React.ReactNode;
  moreMenuItems?: Array<{
    label: string;
    icon?: React.ReactNode;
    onClick: () => void;
  }>;
  isFiltered?: boolean;
  filterNote?: string;
}
export function StatCard({
  title,
  subtitle,
  count,
  actionLabel,
  actionLink,
  onQuickView,
  onSolveIssues,
  children,
  moreMenuItems,
  isFiltered,
  filterNote
}: StatCardProps) {
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const moreMenuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (moreMenuRef.current && !moreMenuRef.current.contains(event.target as Node)) {
        setIsMoreMenuOpen(false);
      }
    }
    if (isMoreMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isMoreMenuOpen]);
  return <Card className="flex flex-col h-full" data-id="element-3965">
      {/* Header */}
      <div className="flex items-start justify-between mb-4" data-id="element-3966">
        <div className="flex-1" data-id="element-3967">
          <div className="flex items-center gap-2 flex-wrap" data-id="element-3968">
            <h3 className="text-base font-bold text-gray-900" data-id="element-3969">{title}</h3>
            {count !== undefined && <span className="text-2xl font-bold text-gray-900" data-id="element-3970">{count}</span>}
            {isFiltered && <motion.div initial={{
            opacity: 0,
            scale: 0.8
          }} animate={{
            opacity: 1,
            scale: 1
          }} className="flex items-center gap-1 bg-amber-100 text-amber-700 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ml-1" data-id="element-3971">
                <Filter size={10} data-id="element-3972" />
                Filtered by site
              </motion.div>}
            {filterNote && <motion.div initial={{
            opacity: 0,
            scale: 0.8
          }} animate={{
            opacity: 1,
            scale: 1
          }} className="flex items-center gap-1 bg-gray-100 text-gray-500 text-[10px] font-semibold px-2 py-0.5 rounded-full ml-1" data-id="element-3973">
                <Info size={10} data-id="element-3974" />
                {filterNote}
              </motion.div>}
          </div>
          {subtitle && <p className="text-xs text-gray-500 mt-0.5" data-id="element-3975">{subtitle}</p>}
        </div>

        <div className="flex items-center gap-2" data-id="element-3976">
          {onQuickView && <button onClick={onQuickView} className="text-gray-400 hover:text-gray-600 transition-colors p-1" title="Quick view" data-id="element-3977">
              <Eye size={18} data-id="element-3978" />
            </button>}

          {onSolveIssues && <button onClick={onSolveIssues} className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500 text-white rounded-lg text-sm font-semibold hover:bg-red-600 transition-colors" data-id="element-3979">
              <AlertCircle size={16} data-id="element-3980" />
              Solve!
            </button>}

          {moreMenuItems && moreMenuItems.length > 0 && <div className="relative" ref={moreMenuRef} data-id="element-3981">
              <button onClick={() => setIsMoreMenuOpen(!isMoreMenuOpen)} className="text-gray-400 hover:text-gray-600 transition-colors p-1" title="More options" data-id="element-3982">
                <MoreVertical size={18} data-id="element-3983" />
              </button>

              <AnimatePresence data-id="element-3984">
                {isMoreMenuOpen && <motion.div initial={{
              opacity: 0,
              y: -10,
              scale: 0.95
            }} animate={{
              opacity: 1,
              y: 0,
              scale: 1
            }} exit={{
              opacity: 0,
              y: -10,
              scale: 0.95
            }} transition={{
              duration: 0.15
            }} className="absolute right-0 top-full mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50" data-id="element-3985">
                    {moreMenuItems.map((item, index) => <button key={index} onClick={() => {
                item.onClick();
                setIsMoreMenuOpen(false);
              }} className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-3" data-id="element-3986">
                        {item.icon && <span className="text-gray-400" data-id="element-3987">{item.icon}</span>}
                        <span data-id="element-3988">{item.label}</span>
                      </button>)}
                  </motion.div>}
              </AnimatePresence>
            </div>}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1" data-id="element-3989">{children}</div>

      {/* Footer Action */}
      {actionLabel && <div className="mt-4 pt-4 border-t border-gray-100" data-id="element-3990">
          {actionLink ? <Link to={actionLink} className="text-sm text-emerald-600 hover:text-emerald-700 font-medium transition-colors" data-id="element-3991">
              {actionLabel} →
            </Link> : <button className="text-sm text-emerald-600 hover:text-emerald-700 font-medium transition-colors" data-id="element-3992">
              {actionLabel} →
            </button>}
        </div>}
    </Card>;
}